/**
 * Cheapest current models first; later entries are fallbacks when a provider retires a model.
 * Update this file when vendors deprecate IDs - callers retry automatically.
 */
export const GEMINI_MODELS = ["gemini-3.5-flash-lite", "gemini-3.6-flash", "gemini-3.5-flash"] as const;

/** Flash-lite often skips Google Search; research uses these instead. */
export const GEMINI_SEARCH_MODELS = GEMINI_MODELS.filter((model) => !model.includes("lite"));

export const OPENAI_MODELS = ["gpt-4o-mini", "gpt-4.1-mini"] as const;

export const DEEPSEEK_MODELS = ["deepseek-v4-flash"] as const;

export type GeminiModel = (typeof GEMINI_MODELS)[number];
export type OpenAiModel = (typeof OPENAI_MODELS)[number];
export type DeepSeekModel = (typeof DEEPSEEK_MODELS)[number];

export type AiProviderId = "deepseek" | "openai" | "gemini";

export interface AiModelChoice {
    id: string;
    provider: AiProviderId;
    providerLabel: string;
    model: string;
    label: string;
}

export const AI_PROVIDER_LABELS: Record<AiProviderId, string> = {
    deepseek: "DeepSeek",
    openai: "OpenAI",
    gemini: "Gemini",
};

export const AI_PROVIDER_MODELS: Record<AiProviderId, readonly string[]> = {
    deepseek: DEEPSEEK_MODELS,
    openai: OPENAI_MODELS,
    gemini: GEMINI_MODELS,
};

/** Consent write order: DeepSeek first, then OpenAI, then Gemini. */
export const AI_PROVIDER_ORDER = ["deepseek", "openai", "gemini"] as const satisfies readonly AiProviderId[];

/** Providers that can run live web search. DeepSeek cannot. */
export const SEARCH_AI_PROVIDERS = ["openai", "gemini"] as const satisfies readonly AiProviderId[];

export function isSearchCapableProvider(provider: AiProviderId): boolean {
    return provider === "openai" || provider === "gemini";
}

export function isSearchCapableAiChoice(choice: Pick<AiModelChoice, "provider" | "model">): boolean {
    if (choice.provider === "deepseek") return false;
    if (choice.provider === "gemini") return !choice.model.includes("lite");
    return choice.provider === "openai";
}

export function makeAiModelId(provider: AiProviderId, model: string): string {
    return `${provider}:${model}`;
}

export function parseAiModelId(id: string): { provider: AiProviderId; model: string } | null {
    const separator = id.indexOf(":");
    if (separator <= 0) return null;
    const provider = id.slice(0, separator);
    const model = id.slice(separator + 1);
    if (!model) return null;
    if (provider !== "deepseek" && provider !== "openai" && provider !== "gemini") return null;
    return { provider, model };
}

export function listAiModelChoices(): AiModelChoice[] {
    return AI_PROVIDER_ORDER.flatMap((provider) =>
        AI_PROVIDER_MODELS[provider].map((model) => ({
            id: makeAiModelId(provider, model),
            provider,
            providerLabel: AI_PROVIDER_LABELS[provider],
            model,
            label: `${AI_PROVIDER_LABELS[provider]} · ${model}`,
        }))
    );
}

/** Put the user's pick first, then the rest of that provider's fallback list. */
export function modelsWithPreferred(preferred: string | undefined, models: readonly string[]): string[] {
    if (!preferred) return [...models];
    return [preferred, ...models.filter((model) => model !== preferred)];
}

/** True when we should try the next model in the fallback list. */
export function isModelUnavailableError(error: unknown): boolean {
    const message = (error instanceof Error ? error.message : String(error)).toLowerCase();

    if (/\b404\b/.test(message) || /\b410\b/.test(message)) {
        return true;
    }

    return (
        message.includes("no longer available") ||
        message.includes("model not found") ||
        message.includes("resource_not_found") ||
        message.includes("does not exist") ||
        (message.includes("deprecated") && message.includes("model"))
    );
}

export async function withModelFallback<T>(
    models: readonly string[],
    providerLabel: string,
    attempt: (model: string) => Promise<T>
): Promise<T> {
    let lastError: Error | undefined;

    for (const model of models) {
        try {
            return await attempt(model);
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            if (!isModelUnavailableError(lastError)) {
                throw lastError;
            }
        }
    }

    throw lastError ?? new Error(`${providerLabel}: no models configured.`);
}
